import React, { useRef, useEffect } from 'react';
import * as THREE from 'three';
import { MagnetArrangement } from '@/utils/magnetCalculatorAdvanced';

interface Magnet3DVisualizationProps {
  containerWidth: number;
  containerHeight: number;
  containerDepth: number;
  magnetDiameter: number;
  magnetThickness: number;
  arrangement: MagnetArrangement;
  showMagneticField?: boolean;
  rotationEnabled?: boolean;
}

export const Magnet3DVisualization: React.FC<Magnet3DVisualizationProps> = ({
  containerWidth,
  containerHeight,
  containerDepth,
  magnetDiameter,
  magnetThickness,
  arrangement,
  showMagneticField = false,
  rotationEnabled = true
}) => {
  const mountRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const containerMeshRef = useRef<THREE.Mesh | null>(null);
  const frameIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (!mountRef.current) return;

    // Inicjalizacja sceny
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x1a1a1a);
    sceneRef.current = scene;

    // Kamera
    const camera = new THREE.PerspectiveCamera(
      75,
      mountRef.current.clientWidth / mountRef.current.clientHeight,
      0.1,
      2000
    );
    // Ustaw kamerę tak aby widzieć cały pojemnik
    const maxDimension = Math.max(containerWidth, containerHeight, containerDepth);
    camera.position.set(
      maxDimension * 0.8,
      maxDimension * 0.6,
      maxDimension * 1.2
    );
    camera.lookAt(0, containerDepth / 2, 0);
    cameraRef.current = camera;

    // Renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(mountRef.current.clientWidth, mountRef.current.clientHeight);
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    mountRef.current.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Światła
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(50, 50, 50);
    directionalLight.castShadow = true;
    directionalLight.shadow.camera.left = -containerWidth;
    directionalLight.shadow.camera.right = containerWidth;
    directionalLight.shadow.camera.top = containerHeight;
    directionalLight.shadow.camera.bottom = -containerHeight;
    scene.add(directionalLight);

    // Pojemnik (przezroczysty) - poprawione wymiary
    const containerGeometry = new THREE.BoxGeometry(
      containerWidth,
      containerDepth,
      containerHeight
    );
    const containerMaterial = new THREE.MeshPhysicalMaterial({
      color: 0x3b82f6,
      transparent: true,
      opacity: 0.1,
      roughness: 0.1,
      metalness: 0.1,
      side: THREE.DoubleSide
    });
    const containerMesh = new THREE.Mesh(containerGeometry, containerMaterial);
    containerMesh.position.set(0, containerDepth / 2, 0);
    scene.add(containerMesh);
    containerMeshRef.current = containerMesh;

    // Krawędzie pojemnika
    const edges = new THREE.EdgesGeometry(containerGeometry);
    const edgesMaterial = new THREE.LineBasicMaterial({ color: 0x60a5fa });
    const edgesMesh = new THREE.LineSegments(edges, edgesMaterial);
    edgesMesh.position.copy(containerMesh.position);
    scene.add(edgesMesh);

    // Powierzchnia stalowa (tył pojemnika)
    const steelGeometry = new THREE.PlaneGeometry(containerWidth * 1.2, containerHeight * 1.2);
    const steelMaterial = new THREE.MeshStandardMaterial({
      color: 0x4a5568,
      metalness: 0.8,
      roughness: 0.2
    });
    const steelMesh = new THREE.Mesh(steelGeometry, steelMaterial);
    steelMesh.position.set(0, 0, -containerHeight / 2 - 5);
    steelMesh.receiveShadow = true;
    scene.add(steelMesh);

    // Magnesy - cylinder w Three.js jest domyślnie pionowy (wzdłuż osi Y)
    const magnetGeometry = new THREE.CylinderGeometry(
      magnetDiameter / 2,
      magnetDiameter / 2,
      magnetThickness,
      32
    );

    // Debug - sprawdź czy arrangement ma pozycje
    console.log('Arrangement positions:', arrangement.positions);
    console.log('Container dimensions:', { containerWidth, containerHeight, containerDepth });

    if (arrangement.positions && arrangement.positions.length > 0) {
      arrangement.positions.forEach((pos, index) => {
        // Sprawdź czy pozycja jest prawidłowa
        if (!pos || typeof pos.x !== 'number' || typeof pos.y !== 'number') {
          console.warn('Invalid position:', pos);
          return;
        }
        
        // Główny magnes
        const magnetMaterial = new THREE.MeshPhysicalMaterial({
          color: index % 2 === 0 ? 0xef4444 : 0x3b82f6, // Czerwony = N, Niebieski = S
          metalness: 0.9,
          roughness: 0.1,
          clearcoat: 1.0,
          clearcoatRoughness: 0.1
        });
        
        const magnetMesh = new THREE.Mesh(magnetGeometry, magnetMaterial);
        // Cylinder jest pionowy (wzdłuż Y), więc nie trzeba go obracać dla poziomego ułożenia
        
        // Pozycje są względem lewego górnego rogu, trzeba je przekształcić
        const x = pos.x - containerWidth / 2;
        const y = 1; // Magnesy na dnie pojemnika
        const z = pos.y - containerHeight / 2;
        
        magnetMesh.position.set(x, y, z);
        magnetMesh.castShadow = true;
        scene.add(magnetMesh);
        
        console.log(`Magnet ${index} at:`, { x, y, z });

        // Pierścień wskazujący biegun
        const ringGeometry = new THREE.TorusGeometry(
          magnetDiameter / 2 + 2,
          1,
          8,
          32
        );
        const ringMaterial = new THREE.MeshBasicMaterial({
          color: index % 2 === 0 ? 0xfca5a5 : 0x93c5fd,
          transparent: true,
          opacity: 0.6
        });
        const ringMesh = new THREE.Mesh(ringGeometry, ringMaterial);
        ringMesh.rotation.x = Math.PI / 2;
        ringMesh.position.copy(magnetMesh.position);
        scene.add(ringMesh);

        // Etykieta z numerem
        if (arrangement.positions.length <= 20) { // Pokazuj numery tylko dla małej liczby magnesów
          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');
          canvas.width = 64;
          canvas.height = 64;
          
          if (context) {
            context.fillStyle = 'white';
            context.font = 'bold 48px Arial';
            context.textAlign = 'center';
            context.textBaseline = 'middle';
            context.fillText((index + 1).toString(), 32, 32);
          }

          const texture = new THREE.CanvasTexture(canvas);
          const spriteMaterial = new THREE.SpriteMaterial({
            map: texture,
            transparent: true
          });
          const sprite = new THREE.Sprite(spriteMaterial);
          sprite.scale.set(10, 10, 1);
          sprite.position.set(
            magnetMesh.position.x,
            magnetMesh.position.y + magnetThickness + 5,
            magnetMesh.position.z
          );
          scene.add(sprite);
        }
      });
    } else {
      console.warn('No magnet positions to display');
    }

    // Wizualizacja pola magnetycznego (opcjonalna)
    if (showMagneticField && arrangement.positions.length > 0) {
      arrangement.positions.forEach((pos) => {
        // Sprawdź czy pozycja jest prawidłowa
        if (!pos || typeof pos.x !== 'number' || typeof pos.y !== 'number') return;
        
        const fieldLines = 6; // Zmniejszona liczba linii dla wydajności
        for (let i = 0; i < fieldLines; i++) {
          try {
            const angle = (i / fieldLines) * Math.PI * 2;
            const radius = magnetDiameter * 0.8;
            
            // Upewnij się że wszystkie wartości są liczbami
            const startX = (pos.x - containerWidth / 2) + Math.cos(angle) * radius;
            const startZ = (pos.y - containerHeight / 2) + Math.sin(angle) * radius;
            const midX = (pos.x - containerWidth / 2) + Math.cos(angle) * radius * 2;
            const midZ = (pos.y - containerHeight / 2) + Math.sin(angle) * radius * 2;
            const endX = (pos.x - containerWidth / 2) + Math.cos(angle) * radius * 2.5;
            const endZ = (pos.y - containerHeight / 2) + Math.sin(angle) * radius * 2.5;
            
            // Sprawdź czy wartości są prawidłowe
            if (isNaN(startX) || isNaN(startZ) || isNaN(midX) || isNaN(midZ) || isNaN(endX) || isNaN(endZ)) {
              continue;
            }
            
            const points = [
              new THREE.Vector3(startX, magnetThickness, startZ),
              new THREE.Vector3(midX, magnetThickness + 20, midZ),
              new THREE.Vector3(endX, magnetThickness + 10, endZ)
            ];
            
            // Sprawdź czy punkty są prawidłowe
            if (points.some(p => !p || isNaN(p.x) || isNaN(p.y) || isNaN(p.z))) {
              continue;
            }
            
            const curve = new THREE.CatmullRomCurve3(points);
            const tubeGeometry = new THREE.TubeGeometry(curve, 10, 0.3, 6, false);
            const tubeMaterial = new THREE.MeshBasicMaterial({
              color: 0xfbbf24,
              transparent: true,
              opacity: 0.2
            });
            const tubeMesh = new THREE.Mesh(tubeGeometry, tubeMaterial);
            scene.add(tubeMesh);
          } catch (err) {
            console.warn('Błąd przy tworzeniu linii pola magnetycznego:', err);
          }
        }
      });
    }

    // Siatka podłoża
    const gridHelper = new THREE.GridHelper(
      Math.max(containerWidth, containerHeight) * 2,
      20,
      0x444444,
      0x222222
    );
    gridHelper.position.y = -5;
    scene.add(gridHelper);

    // Osie współrzędnych
    const axesHelper = new THREE.AxesHelper(50);
    axesHelper.position.set(
      -containerWidth / 2 - 20,
      0,
      -containerHeight / 2 - 20
    );
    scene.add(axesHelper);

    // Animacja
    let mouseX = 0;
    let mouseY = 0;

    const handleMouseMove = (event: MouseEvent) => {
      if (!mountRef.current) return;
      const rect = mountRef.current.getBoundingClientRect();
      mouseX = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouseY = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    };

    if (rotationEnabled) {
      mountRef.current.addEventListener('mousemove', handleMouseMove);
    }

    const animate = () => {
      frameIdRef.current = requestAnimationFrame(animate);

      if (rotationEnabled && containerMeshRef.current) {
        // Delikatna rotacja automatyczna
        containerMeshRef.current.rotation.y += 0.002;
        
        // Rotacja na podstawie pozycji myszy
        if (cameraRef.current) {
          const targetX = mouseX * 100;
          const targetY = mouseY * 50;
          cameraRef.current.position.x += (targetX - cameraRef.current.position.x) * 0.05;
          cameraRef.current.position.y += (targetY + 50 - cameraRef.current.position.y) * 0.05;
          cameraRef.current.lookAt(0, containerDepth / 2, 0);
        }
      }

      if (rendererRef.current && sceneRef.current && cameraRef.current) {
        rendererRef.current.render(sceneRef.current, cameraRef.current);
      }
    };

    animate();

    // Obsługa zmiany rozmiaru
    const handleResize = () => {
      if (!mountRef.current || !cameraRef.current || !rendererRef.current) return;
      
      cameraRef.current.aspect = mountRef.current.clientWidth / mountRef.current.clientHeight;
      cameraRef.current.updateProjectionMatrix();
      rendererRef.current.setSize(
        mountRef.current.clientWidth,
        mountRef.current.clientHeight
      );
    };

    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      if (frameIdRef.current) {
        cancelAnimationFrame(frameIdRef.current);
      }
      if (mountRef.current) {
        mountRef.current.removeEventListener('mousemove', handleMouseMove);
        if (rendererRef.current) {
          mountRef.current.removeChild(rendererRef.current.domElement);
        }
      }
      window.removeEventListener('resize', handleResize);
      
      // Dispose Three.js objects
      if (rendererRef.current) {
        rendererRef.current.dispose();
      }
    };
  }, [
    containerWidth,
    containerHeight,
    containerDepth,
    magnetDiameter,
    magnetThickness,
    arrangement,
    showMagneticField,
    rotationEnabled
  ]);

  return (
    <div 
      ref={mountRef} 
      className="w-full h-full min-h-[400px] rounded-lg overflow-hidden bg-zinc-900"
      style={{ cursor: rotationEnabled ? 'grab' : 'default' }}
    />
  );
};
